import { Box, Paper } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FC, useMemo } from "react";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import type { CrudColumnDef } from "@/components/tables/crudColumnDef";
import { getRecordMetadataColumns } from "@/components/tables/recordMetadataColumns";
import { Link } from "react-router-dom";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import dayjs from "dayjs";
import styles from "@/components/subjectOverview/subjectOverviewAppointmentRequests.module.css";
import {
  getStatusLabel,
  getTranslatedStatusOptions,
} from "@/helper/statusOptions";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { appointmentRequestEntityAdapter } from "@/components/appointmentRequest/appointmentRequestEntityAdapter";

interface Props {
  detailedSubject?: any;
  openWithSpecificSubjectIdUrl?: boolean;
}

const SubjectOverviewAppointmentRequests: FC<Props> = ({
  detailedSubject,
  openWithSpecificSubjectIdUrl = false,
}) => {
  const { t } = useTranslation();
  const statusFilterOptions = getTranslatedStatusOptions("appointmentRequest");

  const customTopToolbarAction = () => {
    const subjectId = detailedSubject?.id;
    const linkTo =
      openWithSpecificSubjectIdUrl && subjectId
        ? `/appointmentrequest/${subjectId}`
        : "/appointmentrequest";
    return (
      <GenericButton startIcon={<OpenInNewIcon />}>
        <Link
          to={linkTo}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link_white}
        >
          {t("appointmentrequest_multi")}
        </Link>
      </GenericButton>
    );
  };

  const columns = useMemo<CrudColumnDef[]>(
    () => [
      {
        accessorKey: "id",
        header: t("id"),
        id: "id",
      },
      {
        header: t("date"),
        accessorKey: "day_set",
        id: "day_set",
        customSortingKey: "day_set__start",
        filterVariant: "date",
        filterDateRangeFormat: "date",
        customColumnFilterId: "start_day",
        setNoFilterParameter: true,
        sortingFn: "datetime",
        accessorFn: (row: any) => {
          const daysInRange = row.day_set.length;
          let date;
          if (row.day_set.length !== 0) {
            const set = row.day_set;
            const start = dayjs(set[0].start).format("DD.MM.YYYY");
            const end = dayjs(set[daysInRange - 1].end).format("DD.MM.YYYY");

            if (start === end) {
              date = start;
            } else {
              date = start + " - " + end;
            }
            // }
            return (
              <Box>
                <Link
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.link_purple}
                  to={`/editappointmentrequest/${row.id}/building`}
                >
                  {date}
                </Link>
              </Box>
            );
          }
        },
      },
      {
        header: t("department"),
        accessorKey: "department_set",
        id: "department_set",
        customColumnFilterId: "department_set__name",
        customSortingKey: "department_name",
        accessorFn: (row: any) => {
          const departments = [];
          if (row.department_set) {
            for (const key in row.department_set) {
              if (row.department_set[key].name) {
                departments.push(row.department_set[key].name);
              }
            }
          }
          return departments.join(", ");
        },
      },
      {
        header: t("category"),
        accessorKey: "category",
        id: "category",
        customColumnFilterId: "category_name",
        accessorFn: (row: any) => {
          if (row.category) {
            const name = row.category.name;
            return name;
          }
        },
      },
      {
        header: t("name"),
        accessorKey: "title",
        id: "title",
      },
      {
        header: t("status"),
        accessorKey: "status",
        id: "status",
        filterVariant: "select",
        filterSelectOptions: statusFilterOptions,
        accessorFn: (row: any) => {
          const appointmentStatus = getStatusLabel(row?.status);
          return <span>{appointmentStatus}</span>;
        },
      },
      {
        header: t("executed_by"),
        accessorKey: "executed_by",
        id: "executed_by",
        customColumnFilterId: "executed_by_name",
        customSortingKey: "executed_by_name",
        entity: "user",
        accessorFn: (row: any) => {
          if (row.executed_by && row.executed_by !== null) {
            return row.executed_by.name;
          } else return "-";
        },
      },
      {
        header: t("assisted_by"),
        accessorKey: "assisted_by",
        id: "assisted_by",
        customColumnFilterId: "assisted_by_name",
        customSortingKey: "assisted_by_name",
        entity: "user",
        accessorFn: (row: any) => {
          if (row.assisted_by && row.assisted_by !== null) {
            return row.assisted_by.name;
          } else return "-";
        },
      },
      ...getRecordMetadataColumns(t, ["created_on", "created_by", "modified_on", "modified_by"]),
    ],
    [],
  );

  const additionalUrlParameter = detailedSubject?.is_company
    ? { company_subject_set__id__in: detailedSubject?.id }
    : { subject_with_sent_invitation_set__id__in: detailedSubject?.id };

  return (
    <Paper className={styles.paperContainer}>
      {/* <h5>{t("appointmentrequest_multi")}</h5> */}
      <GenericCrudTable
        entity={"appointmentRequest"}
        entityAdapter={appointmentRequestEntityAdapter}
        disableAddItem
        columns={columns}
        customTopToolBarAction={() => customTopToolbarAction()}
        customGlobalFilterPosition={"left"}
        customDensity="compact"
        customPageSize={5}
        showRowActions={false}
        additionalUrlParameter={additionalUrlParameter}
        tableCollapseWidth={"75%"}
      />
    </Paper>
  );
};

export default SubjectOverviewAppointmentRequests;
